import React, { useState } from 'react';
import { Mail, ArrowLeft, Loader2, CheckCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../lib/constants';

const ForgotPasswordForm = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) throw error;

      setEmailSent(true);
      toast.success('Password reset link sent!');
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.message || 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  if (emailSent) {
    return (
      <div className="w-full max-w-md mx-auto px-4 sm:px-6 md:px-8">
        <div className="backdrop-blur-lg bg-white/40 border border-cream-300/50 rounded-2xl sm:rounded-3xl shadow-2xl p-6 sm:p-8 md:p-10">
          <div className="text-center">
            {/* Success Icon */}
            <div className="mx-auto w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center mb-4 sm:mb-6 shadow-lg">
              <CheckCircle className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
            </div>
            
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-brown-900 mb-2 sm:mb-3">
              Check Your Email
            </h2>
            
            <p className="text-sm sm:text-base text-brown-700 mb-4 sm:mb-6">
              We've sent a password reset link to
            </p>
            
            <p className="text-base sm:text-lg font-semibold text-brown-900 mb-6 sm:mb-8 break-all">
              {email}
            </p>
            
            <div className="space-y-3 sm:space-y-4">
              <p className="text-xs sm:text-sm text-brown-600">
                Didn't receive the email? Check your spam folder or try again.
              </p>
              
              <button
                onClick={() => setEmailSent(false)}
                className="text-sm sm:text-base text-brown-700 hover:text-brown-900 font-medium transition-colors"
              >
                Try different email
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-md mx-auto px-4 sm:px-6 md:px-8">
      <div className="backdrop-blur-lg bg-white/40 border border-cream-300/50 rounded-2xl sm:rounded-3xl shadow-2xl p-6 sm:p-8 md:p-10">
        
        {/* Back Button */}
        <div className="mb-4 sm:mb-6">
          <button
            onClick={() => navigate(ROUTES.LOGIN)}
            className="flex items-center text-brown-600 hover:text-brown-900 transition-colors text-sm sm:text-base"
          >
            <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
            Back to Login
          </button>
        </div>

        {/* Logo/Brand */}
        <div className="text-center mb-6 sm:mb-8">
          <div className="flex justify-center mb-6 sm:mb-8">
          <div className="rounded-full bg-gradient-to-br from-brown-500 to-brown-700 border-4 border-brown-100 shadow-lg flex items-center justify-center aspect-square overflow-hidden w-16 sm:w-18 md:w-20 lg:w-24">
            <img 
              src="/book.png" 
              alt="OmniClarix - Clarity, Simplified." 
              className="w-auto h-4/5 object-contain pointer-events-none"
            />
          </div>
        </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-brown-900 mb-2 sm:mb-3">
            Forgot Password?
          </h2>
          <p className="text-sm sm:text-base text-brown-600 px-2">
            No worries! Enter your email and we'll send you a reset link.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6">
          {/* Email Field */}
          <div>
            <label htmlFor="email" className="block text-sm sm:text-base font-medium text-brown-800 mb-2">
              Email Address
            </label>
            <div className="relative">
              <Mail className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 text-brown-400 w-4 h-4 sm:w-5 sm:h-5" />
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-2.5 sm:py-3 md:py-3.5 
                  bg-white/60 backdrop-blur-sm border-2 border-cream-300 rounded-lg sm:rounded-xl 
                  focus:ring-2 focus:ring-brown-500 focus:border-brown-500 
                  outline-none transition-all text-sm sm:text-base
                  placeholder:text-brown-300 text-brown-900"
                disabled={loading}
                required
              />
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-brown-600 to-brown-700 hover:from-brown-700 hover:to-brown-800 
              text-white font-medium py-2.5 sm:py-3 md:py-3.5 px-4 rounded-lg sm:rounded-xl 
              transition-all duration-200 flex items-center justify-center 
              disabled:opacity-50 disabled:cursor-not-allowed shadow-lg
              text-sm sm:text-base md:text-lg"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 mr-2 animate-spin" />
                <span>Sending...</span>
              </>
            ) : (
              'Send Reset Link'
            )}
          </button>
        </form>

        {/* Back to Login */}
        <div className="mt-6 sm:mt-8 text-center">
          <p className="text-xs sm:text-sm text-brown-600">
            Remember your password?{' '}
            <button
              onClick={() => navigate(ROUTES.LOGIN)}
              className="text-brown-700 hover:text-brown-900 font-semibold transition-colors"
            >
              Sign in
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordForm;